import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import BackButton from "../../Button/BackButton";
import ShowToast from "../../Common/Toast";
import useBiddingStore from "../useBiddingStore";
import ProductImagePlaceholder from "../ProductImagePlaceholder";
import { STATUS_PILL_LABEL } from "utils/constants/bids";
import "./BidDetails.css";
import {
  BID_ITEM_ACTION,
  LIVE_TIMER_INTERVAL_MS,
  MINUTES_IN_HOUR,
  MS_IN_MINUTE,
  MS_IN_SECOND,
  ONE_HOUR_IN_MS,
  SECONDS_IN_MINUTE,
  TWELVE_HOURS_IN_MS,
} from "../constants.bids";
import {
  DetailPageWrapper,
  DetailTopBar,
  DetailTopBarInfo,
  DetailTopBarTitle,
  DetailTopBarSub,
  IconBtn,
  DetailContent,
  DetailHeaderCard,
  DetailCardTitleRow,
  DetailCardTitle,
  DetailHeaderBody,
  DetailStatsRow,
  DetailStatBox,
  DetailStatSubLabel,
  DetailDashedDivider,
  DetailAddressRow,
  DetailAddressIconBox,
  StatusPill,
  BidMetaIcon,
  BidMetaLabel,
  BidTimingText,
  SectionCard,
  SectionDividerLabel,
  ProductListItem,
  ProductImg,
  ProductInfo,
  ProductName,
  ProductMeta,
  LiveProductActionCard,
  LiveProductActionInfo,
  LiveProductActionDivider,
  LiveProductActionButtonWrap,
  LiveProductActionButton,
  AccordionHeader,
  AccordionTitle,
  DidNotBidSection,
  DidNotBidLabel,
  ProductThumbsRow,
  ProductThumb,
  ProductThumbMore,
  SeeDetailsLink,
  CtaButton,
  Overlay,
} from "../styles.bids";

const getItemUnit = (item = {}) => item.unit_of_measurement || item.sub_uom || item.uom || "kg";
const getTaxPercentage = (item = {}) => Number(item.tax_percentage ?? item.tax_rate ?? item.taxRate ?? 0) || 0;
const ADDRESS_ICON_SRC = "/logos/bid_address.svg";
const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value ?? "";

  return amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
};

const formatTime = (date) => {
  const options = date.getMinutes() === 0
    ? { hour: "numeric" }
    : { hour: "numeric", minute: "2-digit" };

  return date.toLocaleTimeString("en-IN", options).replace(/\s?(am|pm)$/i, (match) => ` ${match.trim().toUpperCase()}`);
};

const sanitizePositiveDecimal = (value) => {
  const cleaned = value.replace(/[^\d.]/g, "");
  const [whole, ...decimalParts] = cleaned.split(".");
  return decimalParts.length ? `${whole}.${decimalParts.join("")}` : whole;
};

const sanitizePositiveInteger = (value) => value.replace(/\D/g, "");

/* ─── Icons ──────────────────────────────────────────────────── */

const LocationIcon = () => <img src="/logos/bid_location.svg" width="22" height="22" alt="" />;
const CalendarIcon = () => <img src="/logos/bids_upcoming.svg" width="22" height="22" alt="" />;
const RupeeIcon    = () => <img src="/logos/bid_amount.svg"    width="22" height="22" alt="" />;

const CallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M4 2h4l2 5-2.5 1.5C8.5 11 9 11.5 11.5 13.5L13 11l5 2v4c0 1-5 3-11-3S3 3 4 2z" stroke="#333" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="8" cy="8" r="6" stroke="#333" strokeWidth="1.5" fill="none"/>
    <path d="M13 13l3.5 3.5" stroke="#333" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SaveIcon = ({ color = "#ef4f5f" }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="1" width="12" height="12" rx="2" stroke={color} strokeWidth="1.3" fill="none"/>
    <path d="M4 1v4h6V1" stroke={color} strokeWidth="1.3"/>
    <rect x="3" y="7" width="8" height="5" rx="1" stroke={color} strokeWidth="1.3" fill="none"/>
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={cx("bid-details-chevron-icon", open && "bid-details-chevron-icon-open")}
  >
    <path d="M4 6l4 4 4-4" stroke="#999" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ─── Add Price & Quantity bottom sheet ──────────────────────── */

const AddPriceSheet = ({ product, existingBid, onSave, onDelete, onClose, readOnly = false }) => {
  const [qty, setQty] = useState(existingBid?.quantity_offered != null ? String(existingBid.quantity_offered) : "");
  const [price, setPrice] = useState(existingBid?.price_offered != null ? String(existingBid.price_offered) : "");

  const isEditing = !!existingBid;
  const unit = product.unit || "kg";
  const taxRate = getTaxPercentage(product);
  const qtyValue = qty.trim() === "" ? null : Number(qty);
  const priceValue = price.trim() === "" ? null : Number(price);
  const priceWithTax = taxRate && Number.isFinite(priceValue) && priceValue > 0
    ? (priceValue * (1 + taxRate / 100)).toFixed(2)
    : null;
  const minQty = Number.isFinite(product.minQtyValue) ? product.minQtyValue : null;
  const maxQty = Number.isFinite(product.requiredQtyValue) ? product.requiredQtyValue : null;
  const targetPrice = Number.isFinite(product.targetPriceValue) ? product.targetPriceValue : null;
  const minPrice = targetPrice != null ? targetPrice * 0.5 : null;
  const maxPrice = targetPrice != null ? targetPrice * 2 : null;

  const qtyError = (() => {
    if (qty.trim() === "") return "";
    if (!Number.isFinite(qtyValue) || qtyValue <= 0) return "Enter a valid quantity";
    if (minQty != null && qtyValue < minQty) return `Minimum bid quantity is ${minQty}${unit}`;
    if (maxQty != null && qtyValue > maxQty) return `Bid quantity cannot exceed ${maxQty}${unit}`;
    return "";
  })();

  const priceError = (() => {
    if (price.trim() === "") return "";
    if (!Number.isFinite(priceValue) || priceValue <= 0) return "Enter a valid price";
    if (minPrice != null && priceValue < minPrice) return "The price entered is significantly lower. Please check.";
    if (maxPrice != null && priceValue > maxPrice) return "The price entered is unusually high. Please check.";
    return "";
  })();

  const canSave = qty.trim() !== "" && price.trim() !== "" && !qtyError && !priceError;

  const handleSave = () => {
    if (readOnly || !canSave) return;
    onSave({
      bidding_session_item_id: product.bidding_session_item_id,
      quantity_offered: parseFloat(qty),
      price_offered: parseFloat(price),
    });
    onClose();
  };

  const handleDelete = () => {
    if (readOnly) return;
    onDelete(product.bidding_session_item_id);
    onClose();
  };

  return (
    <Overlay onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bid-details-add-price-sheet"
      >
        {/* scrollable body */}
        <div className="bid-details-add-price-body">

          {/* title row */}
          <div className="bid-details-add-price-title-row">
            <span className="bid-details-add-price-title">
              {readOnly ? "Bid details" : "Add price & quantity"}
            </span>
            <button onClick={onClose} className="bid-details-add-price-close">×</button>
          </div>

          {/* ONE white card — product + divider + bullets + inputs */}
          <div className="bid-details-add-price-card">

            {/* product row */}
            <div className="bid-details-add-price-product-row">
              <div className="bid-details-add-price-product-image-wrap">
                {product.imageUrl
                  ? <img src={product.imageUrl} alt={product.name} className="bid-details-product-image bid-details-product-image-radius-12" />
                  : <ProductImagePlaceholder />}
              </div>
              <div className="bid-details-add-price-product-info">
                <div className="bid-details-add-price-product-name">{product.name}</div>
                <div className="bid-details-add-price-product-meta">
                  Min qty: {product.minQty} • Total qty: {product.totalQty}
                </div>
              </div>
            </div>

            {/* divider */}
            <div className="bid-details-divider-solid" />

            {/* description bullets */}
            {product.description && (
              <div className="bid-details-description-box">
                {product.description.split(/[.\n]/).filter(Boolean).slice(0, 3).map((line, i) => (
                  <div key={i} className={cx("bid-details-description-line", i < 2 && "bid-details-description-line-spaced")}>
                    <span className="bid-details-description-text">{line.trim()}</span>
                  </div>
                ))}
              </div>
            )}

            {/* inputs */}
            <div className="bid-details-add-price-inputs">
              <fieldset className="bid-details-bid-field">
                <legend className="bid-details-bid-field-legend bid-details-bid-field-legend-light">
                  Bid quantity
                </legend>
                <div className="bid-details-bid-field-control">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={qty}
                    onChange={e => setQty(sanitizePositiveInteger(e.target.value))}
                    disabled={readOnly}
                    placeholder="0"
                    className="bid-details-bid-input"
                  />
                  <span className="bid-details-bid-unit">{unit}</span>
                </div>
              </fieldset>
              {qtyError && (
                <div className="bid-details-bid-error">
                  {qtyError}
                </div>
              )}

              <fieldset className="bid-details-bid-field">
                <legend className="bid-details-bid-field-legend">
                  {taxRate ? `Bid price (incl. ${taxRate}% tax)` : "Bid price/unit"}
                </legend>
                <div className="bid-details-bid-field-control">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={e => setPrice(sanitizePositiveDecimal(e.target.value))}
                    disabled={readOnly}
                    placeholder="0"
                    className="bid-details-bid-input"
                  />
                  <span className="bid-details-bid-unit">₹/{unit}</span>
                </div>
              </fieldset>
              {priceError ? (
                <div className="bid-details-bid-error">
                  {priceError}
                </div>
              ) : null}
              {priceWithTax ? (
                <div className="bid-details-tax-hint">
                  Amount after {taxRate}% tax: ₹{priceWithTax}/{unit}
                </div>
              ) : null}
            </div>
          </div>

        </div>

        {/* sticky bottom bar */}
        <div className="bid-details-add-price-footer">
          {readOnly ? (
            <button
              onClick={onClose}
              className="bid-details-sheet-primary-button"
            >
              Close
            </button>
          ) : isEditing ? (
            <div className="bid-details-sheet-action-row">
              <button
                onClick={handleDelete}
                className="bid-details-sheet-delete-button"
              >
                Delete bid
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="bid-details-sheet-save-button"
              >
                Save changes
              </button>
            </div>
          ) : (
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="bid-details-sheet-primary-button"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
};

/* ─── Accordion ──────────────────────────────────────────────── */

const ProductItem = ({ product }) => (
  <ProductListItem>
    <ProductImg>
      {product.imageUrl
        ? <img src={product.imageUrl} alt={product.name} className="bid-details-product-image bid-details-product-image-radius-12" />
        : <ProductImagePlaceholder />}
    </ProductImg>
    <ProductInfo>
      <ProductName>{product.name}</ProductName>
      <ProductMeta>
        {product.qty}{product.price ? ` • ${product.price}` : ""}
      </ProductMeta>
    </ProductInfo>
  </ProductListItem>
);

const Accordion = ({ title, products, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <AccordionHeader $open={open} onClick={() => setOpen((o) => !o)}>
        <AccordionTitle>{title}</AccordionTitle>
        <ChevronIcon open={open} />
      </AccordionHeader>
      {open && products.map((p, i) => <ProductItem key={p.id || i} product={p} />)}
    </div>
  );
};

/* ─── STATUS config ───────────────────────────────────────────── */

const TOP_BAR_LABELS = {
  live:          { title: "Place bid",          sub: "products in this bid" },
  upcoming:      { title: "Upcoming bid",        sub: "products in this bid" },
  awarded:       { title: "Awarded bid",         sub: "Awarded bid for products" },
  completed:     { title: "Completed bid",       sub: "products in this bid" },
  submitted:     { title: "Submitted bid",       sub: "products in this bid" },
  not_awarded:   { title: "Not submitted bid",   sub: "products in this bid" },
  no_submission: { title: "Not submitted bid",   sub: "products in this bid" },
};

/* ─── Main component ─────────────────────────────────────────── */

const BidDetails = () => {
  const location = useLocation();
  const { bidId } = useParams();
  const navigate = useNavigate();

  const passedBidId = location.state?.bidId || bidId;
  const passedStatus = location.state?.status === "previous" ? null : location.state?.status;

  const {
    currentSession,
    currentSessionStatus,
    currentSessionError,
    vendorBids,
    vendorBidId,
    vendorBidsStatus,
    getBiddingSession,
    getVendorBids,
    upsertBids,
    upsertBidStatus,
    submitBids,
    getBiddingTerms,
    getAllotment,
  } = useBiddingStore();

  const [allotments, setAllotments] = useState([]);
  const [liveTimer, setLiveTimer] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);

  // local bid state: { [bidding_session_item_id]: { quantity_offered, price_offered } }
  const [localBids, setLocalBids] = useState({});
  const [activeProduct, setActiveProduct] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const currentBidIdRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", key: 0 });
  const [modifiedBidItemIds, setModifiedBidItemIds] = useState([]);

  const showErrorToast = (message) => {
    setToast((prev) => ({
      show: true,
      message: message || "Something went wrong",
      key: prev.key + 1,
    }));
  };

  useEffect(() => {
    if (passedBidId) {
      setLocalBids({});
      setModifiedBidItemIds([]);
      currentBidIdRef.current = null;
      getBiddingSession(passedBidId, passedStatus);
      getVendorBids(passedBidId);
    }
  }, [passedBidId, passedStatus]);

  useEffect(() => {
    if (currentSession?.status === "awarded" && passedBidId) {
      getAllotment(passedBidId).then(setAllotments);
    }
  }, [currentSession?.status, passedBidId]);

  useEffect(() => {
    const endTime = currentSession?.endTime;
    const st = currentSession?.status;
    if (!endTime || (st !== "live" && st !== "submitted")) { setLiveTimer(null); return; }

    const update = () => {
      const diff = new Date(endTime) - Date.now();
      if (diff <= 0) {
        const d = new Date(endTime);
        const timeStr = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) + ", " +
          formatTime(d);
        setLiveTimer({ prefix: "Ended on ", suffix: timeStr, urgent: false });
        return false;
      }

      if (diff <= ONE_HOUR_IN_MS) {
        const totalSecs = Math.floor(diff / MS_IN_SECOND);
        const mins = Math.floor(totalSecs / SECONDS_IN_MINUTE);
        const secs = totalSecs % SECONDS_IN_MINUTE;
        setLiveTimer({ prefix: "Ends in ", suffix: `${mins}m ${String(secs).padStart(2, "0")}s`, urgent: true });
      } else if (diff <= TWELVE_HOURS_IN_MS) {
        const totalMins = Math.floor(diff / MS_IN_MINUTE);
        const hrs = Math.floor(totalMins / MINUTES_IN_HOUR);
        const mins = totalMins % MINUTES_IN_HOUR;
        setLiveTimer({ prefix: "Ends in ", suffix: `${hrs}h ${mins}m`, urgent: false });
      } else {
        const d = new Date(endTime);
        const timeStr = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) + ", " +
          formatTime(d);
        setLiveTimer({ prefix: "Ends by ", suffix: timeStr, urgent: false });
      }

      return true;
    };

    if (!update()) return;

    const id = setInterval(() => {
      if (!update()) clearInterval(id);
    }, LIVE_TIMER_INTERVAL_MS);
    return () => clearInterval(id);
  }, [currentSession?.endTime, currentSession?.status]);

  // seed localBids from server bids once loaded
  useEffect(() => {
    if (vendorBids.length > 0) {
      const seed = {};
      vendorBids.forEach(vb => {
        seed[vb.bidding_session_item_id] = {
          bidding_session_item_id: vb.bidding_session_item_id,
          quantity_offered: vb.quantity_offered,
          price_offered: vb.price_offered,
          id: vb.id,
        };
      });
      setLocalBids(seed);
      setModifiedBidItemIds([]);
    }
    if (vendorBidId) { currentBidIdRef.current = vendorBidId; console.log("[vendorBidId set]", vendorBidId); }
  }, [vendorBids, vendorBidId]);

  if (currentSessionStatus === "BEGIN" || !currentSession) {
    return (
      <DetailPageWrapper>
        <DetailTopBar>
          <BackButton pathName="/bids/all" />
          <DetailTopBarInfo>
            <DetailTopBarTitle>Loading…</DetailTopBarTitle>
          </DetailTopBarInfo>
        </DetailTopBar>
        <div className="bid-details-loading-message">
          {currentSessionStatus === "FAIL"
            ? currentSessionError || "Failed to load bid details"
            : "Loading bid details…"}
        </div>
      </DetailPageWrapper>
    );
  }

  const session = currentSession;
  const status = session.status;
  const contactInfo = {
    name: session.creatorName,
    phone: session.creatorPhoneNumber,
  };

  const products = session.rawItems.map((item) => {
    const unit = getItemUnit(item);

    return {
      id: item.id,
      imageUrl: item.image_url || item.icon_url || item.imageUrl || null,
      name: item.name || `Item ${item.item_id}`,
      qty: item.required_quantity != null ? `${item.required_quantity}${unit}` : "",
      minQty: item.min_award_quantity != null ? `${item.min_award_quantity}${unit}` : "",
      totalQty: item.required_quantity != null ? `${item.required_quantity}${unit}` : "",
      price: item.expected_price ? `₹${item.expected_price}/${unit}` : "",
      minQtyValue: item.min_award_quantity,
      requiredQtyValue: item.required_quantity,
      targetPriceValue: item.expected_price,
      bidding_session_item_id: item.id,
      description: item.description || "",
      itemId: item.item_id || "",
      type: item.type || "",
      weightPerPacket: item.weight_per_packet,
      unit,
      taxRate: getTaxPercentage(item),
    };
  });

  const placedBidProducts = vendorBids.map((vb) => {
    const sessionItem = session.rawItems.find((i) => i.id === vb.bidding_session_item_id);
    const sessionProduct = products.find((p) => p.bidding_session_item_id === vb.bidding_session_item_id);
    const unit = sessionProduct?.unit || getItemUnit(sessionItem);
    const taxRate = sessionProduct?.taxRate || vb.taxRate || getTaxPercentage(sessionItem);
    const priceWithTax = taxRate ? Number(vb.price_offered) * (1 + taxRate / 100) : Number(vb.price_offered);

    return {
      ...sessionProduct,
      id: vb.id || vb.bidding_session_item_id,
      imageUrl: sessionProduct?.imageUrl || sessionItem?.image_url || sessionItem?.icon_url || null,
      name: sessionItem?.name || `Item ${vb.bidding_session_item_id}`,
      minQty: sessionProduct?.minQty || "",
      totalQty: sessionProduct?.totalQty || "",
      qty: `${vb.quantity_offered}${unit}`,
      price: `₹${formatAmount(taxRate ? priceWithTax : vb.price_offered)}/${unit}`,
      bidding_session_item_id: vb.bidding_session_item_id,
      description: sessionProduct?.description || sessionItem?.description || "",
      unit,
      taxRate,
      existingBid: vb,
    };
  });

  const didNotBidProducts = products.filter(
    (p) => !vendorBids.find((vb) => vb.bidding_session_item_id === p.bidding_session_item_id)
  );

  const isLive = status === "live";
  const isUpcoming = status === "upcoming";
  const isAwarded = status === "awarded";
  const isSubmittedOrNotAwarded = status === "submitted" || status === "not_awarded" || status === "no_submission" || status === "completed";

  const topLabel = isLive
    ? TOP_BAR_LABELS.live
    : TOP_BAR_LABELS[status] || TOP_BAR_LABELS.live;
  const pillLabel = STATUS_PILL_LABEL[status] || status;
  const termsDescription = session.description?.trim();
  const termsDescriptionLines = termsDescription
    ? termsDescription.split(/\r?\n/).map((line) => line.trim().replace(/^[•*-]\s*/, "")).filter(Boolean)
    : [];

  // Payment info from session configs
  const processingFee = session.rawConfigs?.find(c => c.key === "processing_fee")?.value;
  const securityDeposit = session.rawConfigs?.find(c => c.key === "security_deposit")?.value;
  const totalFee = session.rawConfigs?.find(c => c.key === "total_fee" || c.key === "total_amount")?.value;

  const addedCount = Object.keys(localBids).length;
  const canContinue = addedCount > 0;
  const hasModifiedBidItems = modifiedBidItemIds.length > 0;

  const filteredProducts = searchQuery.trim()
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : products;
  const isSearchMode = showSearch && searchQuery.trim().length > 0;

  const markBidItemModified = (sessionItemId, isModified = true) => {
    setModifiedBidItemIds((prev) => {
      const normalizedId = String(sessionItemId);
      const next = prev.filter((id) => String(id) !== normalizedId);

      return isModified ? [...next, normalizedId] : next;
    });
  };

  const getServerBidForSessionItem = (sessionItemId) => (
    vendorBids.find((vb) => Number(vb.bidding_session_item_id) === Number(sessionItemId))
  );

  const hasSameBidValues = (bidA, bidB) => (
    Number(bidA?.quantity_offered) === Number(bidB?.quantity_offered) &&
    Number(bidA?.price_offered) === Number(bidB?.price_offered)
  );

  const handleSaveBid = (bidItem) => {
    setLocalBids(prev => ({ ...prev, [bidItem.bidding_session_item_id]: bidItem }));
    const serverBid = getServerBidForSessionItem(bidItem.bidding_session_item_id);
    markBidItemModified(bidItem.bidding_session_item_id, !serverBid || !hasSameBidValues(bidItem, serverBid));
  };

  const handleDeleteBid = (itemId) => {
    setLocalBids(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    markBidItemModified(itemId, !!getServerBidForSessionItem(itemId));
  };

  const handleSaveProgress = (onSaved) => {
    // Only operate on items that belong to this session
    const sessionItemIds = new Set(products.map(p => String(p.bidding_session_item_id)));
    const serverBidMap = {};
    vendorBids.forEach(vb => {
      if (sessionItemIds.has(String(vb.bidding_session_item_id))) {
        serverBidMap[vb.bidding_session_item_id] = vb;
      }
    });

    const bidItems = modifiedBidItemIds.reduce((items, sessionItemId) => {
      if (!sessionItemIds.has(String(sessionItemId))) return items;

      const localBid = localBids[sessionItemId];
      const serverBid = serverBidMap[sessionItemId];

      if (localBid) {
        if (serverBid) {
          items.push({
            id: serverBid.id,
            quantity_offered: localBid.quantity_offered,
            price_offered: localBid.price_offered,
            action: BID_ITEM_ACTION.UPDATE,
          });
        } else {
          items.push({
            bidding_session_item_id: localBid.bidding_session_item_id,
            quantity_offered: localBid.quantity_offered,
            price_offered: localBid.price_offered,
            action: BID_ITEM_ACTION.ADD,
          });
        }

        return items;
      }

      if (serverBid) {
        items.push({
          id: serverBid.id,
          quantity_offered: serverBid.quantity_offered,
          price_offered: serverBid.price_offered,
          action: BID_ITEM_ACTION.DELETE,
        });
      }

      return items;
    }, []);

    const missingBidItemId = bidItems.some((item) =>
      [BID_ITEM_ACTION.UPDATE, BID_ITEM_ACTION.DELETE].includes(item.action) && item.id == null
    );
    if (missingBidItemId) {
      showErrorToast("Unable to save changes. Please refresh and try again.");
      return;
    }

    if (!bidItems.length) {
      setModifiedBidItemIds([]);
      if (onSaved) onSaved();
      return;
    }

    upsertBids(session.id, bidItems,
      (bidDetail) => {
        const id = bidDetail?.bid?.id || bidDetail?.id;
        if (id) { currentBidIdRef.current = id; }
        setModifiedBidItemIds([]);
        if (onSaved) onSaved();
      },
      (err) => showErrorToast(err || "Failed to save progress")
    );
  };

  const handleContinueAfterSave = async () => {
    setTermsLoading(true);
    await getBiddingTerms(session.id);
    setTermsLoading(false);
    setShowTerms(true);
  };

  const handleContinue = () => {
    if (hasModifiedBidItems) {
      handleSaveProgress(handleContinueAfterSave);
      return;
    }

    handleContinueAfterSave();
  };

  const handleAgreeTerms = () => {
    setShowTerms(false);
    submitBids(
      session.id,
      currentBidIdRef.current,
      () => navigate("/bids/submitted"),
      (err) => showErrorToast(err || "Failed to submit bid")
    );
  };

  const renderBidDetailsCard = ({ showEmptyMessage = true } = {}) => (
    <>
      <SectionDividerLabel>BID DETAILS</SectionDividerLabel>
      <SectionCard className="bid-details-section-card-rounded">
        {vendorBidsStatus === "BEGIN" ? (
          <div className="bid-details-card-message bid-details-card-message-center">
            Loading your bids…
          </div>
        ) : placedBidProducts.length > 0 ? (
          <>
            <div className="bid-details-placed-title">
              Placed bid for {placedBidProducts.length} product{placedBidProducts.length > 1 ? "s" : ""}
            </div>
            <div className="bid-details-placed-list">
              {placedBidProducts.map((p, index) => (
                <div
                  key={p.id}
                  className={cx(
                    "bid-details-placed-row",
                    index === 0 && "bid-details-placed-row-first",
                    index < placedBidProducts.length - 1 && "bid-details-placed-row-bordered"
                  )}
                >
                  <ProductImg className="bid-details-product-img-small">
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-8" />
                      : <ProductImagePlaceholder />}
                  </ProductImg>
                  <ProductInfo className="bid-details-min-width-zero">
                    <ProductName className="bid-details-placed-product-name">
                      {p.name}
                    </ProductName>
                    <ProductMeta className="bid-details-placed-product-meta">
                      {p.qty} &bull; {p.price}
                      {p.taxRate ? <span className="bid-details-tax-note"> (incl. {p.taxRate}% tax)</span> : null}
                    </ProductMeta>
                  </ProductInfo>
                </div>
              ))}
            </div>
          </>
        ) : showEmptyMessage ? (
          <div className="bid-details-card-message">
            No bid was submitted for this session.
          </div>
        ) : null}

        {placedBidProducts.length > 0 && didNotBidProducts.length > 0 && (
          <DidNotBidSection className="bid-details-did-not-bid-section">
            <DidNotBidLabel className="bid-details-did-not-bid-label">
              Product{didNotBidProducts.length > 1 ? "s" : ""} you did not bid for
            </DidNotBidLabel>
            <ProductThumbsRow className="bid-details-did-not-bid-thumbs">
              {didNotBidProducts.slice(0, 4).map((p, i) => (
                <ProductThumb key={i} className="bid-details-did-not-bid-thumb">
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-10" />
                    : <ProductImagePlaceholder />}
                </ProductThumb>
              ))}
              {didNotBidProducts.length > 4 && (
                <ProductThumbMore className="bid-details-did-not-bid-thumb-more">
                  +{didNotBidProducts.length - 4} more
                </ProductThumbMore>
              )}
            </ProductThumbsRow>
          </DidNotBidSection>
        )}
        {products.length > 0 && (
          <div className="bid-details-see-products-wrap">
            <SeeDetailsLink
              onClick={() => setShowProductDetails(true)}
              className="bid-details-see-products-link"
            >
              See product details
            </SeeDetailsLink>
          </div>
        )}
      </SectionCard>
    </>
  );

  const renderHeaderCard = () => (
    <DetailHeaderCard>
      <DetailCardTitleRow>
        <DetailCardTitle>{session.title}</DetailCardTitle>
        <StatusPill $status={status}>{pillLabel}</StatusPill>
      </DetailCardTitleRow>

      <DetailHeaderBody>
        <DetailStatsRow>
          <DetailStatBox>
            <BidMetaIcon><LocationIcon /></BidMetaIcon>
            <BidMetaLabel>{session.location}</BidMetaLabel>
            <DetailStatSubLabel>Location</DetailStatSubLabel>
          </DetailStatBox>
          <DetailStatBox>
            <BidMetaIcon><CalendarIcon /></BidMetaIcon>
            <BidMetaLabel>{session.deliveryRange}</BidMetaLabel>
            <DetailStatSubLabel>Delivery by</DetailStatSubLabel>
          </DetailStatBox>
          <DetailStatBox>
            <BidMetaIcon><RupeeIcon /></BidMetaIcon>
            <BidMetaLabel>{session.totalAmount}</BidMetaLabel>
            <DetailStatSubLabel>Total value</DetailStatSubLabel>
          </DetailStatBox>
        </DetailStatsRow>

        {session.address && (
          <>
            <DetailDashedDivider />
            <DetailAddressRow>
              <DetailAddressIconBox>
                <img src={ADDRESS_ICON_SRC} width="44" height="44" alt="" />
              </DetailAddressIconBox>
              <span>{session.address}</span>
            </DetailAddressRow>
          </>
        )}

        <DetailDashedDivider />
        <BidTimingText>
          {status === "upcoming" && session.startTime ? (
            <>Taking bids from <span>{new Date(session.startTime).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span> onwards</>
          ) : liveTimer ? (
            <>
              {liveTimer.prefix}
              <span className={cx(liveTimer.urgent && "bid-details-timer-urgent")}>
                {liveTimer.suffix}
              </span>
            </>
          ) : (() => {
            const isPast = isSubmittedOrNotAwarded || isAwarded;
            const label = isPast ? "Ended on" : "Ends by";
            const timeStr = session.endTime
              ? new Date(session.endTime).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) + ", " + formatTime(new Date(session.endTime))
              : "—";
            return <>{label} <span>{timeStr}</span></>;
          })()}
        </BidTimingText>
      </DetailHeaderBody>
    </DetailHeaderCard>
  );

  const renderReadOnlyProductDetails = () => (
    <>
      {renderHeaderCard()}
      <div className="bid-details-products-heading-wrap">
        <div className="bid-details-products-heading">
          {products.length} products
        </div>
        {placedBidProducts.length > 0 && (
          <div className="bid-details-products-subheading">
            {placedBidProducts.length} product{placedBidProducts.length > 1 ? "s'" : "'s"} price added
          </div>
        )}
      </div>
      {products.map((p) => {
        const bid = localBids[p.bidding_session_item_id] || getServerBidForSessionItem(p.bidding_session_item_id);
        const hasBid = !!bid;

        if (hasBid) {
          return (
            <SectionCard key={p.id} className="bid-details-bid-card">
              <div className="bid-details-bid-card-row">
                <ProductImg className="bid-details-product-img-medium">
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-8" />
                    : <ProductImagePlaceholder />}
                </ProductImg>
                <ProductName className="bid-details-bid-card-product-name">
                  {p.name}
                </ProductName>
                <span className="bid-details-added-pill">
                  <Check size={14} strokeWidth={2.6} />
                  Added
                </span>
              </div>

              <div className="bid-details-dashed-divider" />

              <div className="bid-details-bid-values-grid">
                <div>
                  <div className="bid-details-bid-value-label">Quantity:</div>
                  <div className="bid-details-bid-value">{bid.quantity_offered}{p.unit}</div>
                </div>
                <div>
                  <div className="bid-details-bid-value-label">Price/{p.unit}:</div>
                  <div className="bid-details-bid-value">₹{formatAmount(bid.price_offered)}</div>
                </div>
              </div>
            </SectionCard>
          );
        }

        return (
          <SectionCard key={p.id}>
            <ProductListItem className="bid-details-product-list-item-no-border">
              <ProductImg>
                {p.imageUrl
                  ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-12" />
                  : <ProductImagePlaceholder />}
              </ProductImg>
              <ProductInfo className="bid-details-min-width-zero">
                <ProductName>
                  {p.name}
                </ProductName>
                <ProductMeta>
                  Min qty: {p.minQty || "—"} &bull; Total qty: {p.totalQty || "—"}
                </ProductMeta>
              </ProductInfo>
            </ProductListItem>
          </SectionCard>
        );
      })}
      <div className="bid-details-bottom-spacer-small" />
    </>
  );

  return (
    <DetailPageWrapper>
      {toast.show && (
        <ShowToast key={toast.key} errorMsg={toast.message} type="error" />
      )}

      <DetailTopBar>
        {showProductDetails ? (
          <>
            <button onClick={() => setShowProductDetails(false)} className="bid-details-topbar-back-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M13 16l-6-6 6-6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <DetailTopBarInfo>
              <DetailTopBarTitle>Product details</DetailTopBarTitle>
              <DetailTopBarSub>
                {products.length} products in this bid
              </DetailTopBarSub>
            </DetailTopBarInfo>
          </>
        ) : showSearch ? (
          <>
            <button onClick={() => { setShowSearch(false); setSearchQuery(""); }} className="bid-details-topbar-back-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M13 16l-6-6 6-6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bid-details-search-box">
              <SearchIcon />
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products…"
                className="bid-details-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="bid-details-search-clear">×</button>
              )}
            </div>
          </>
        ) : (
          <>
            <BackButton pathName="/bids/all" />
            <DetailTopBarInfo>
              <DetailTopBarTitle>BID#{session.id}</DetailTopBarTitle>
              <DetailTopBarSub>
                {products.length} {topLabel.sub}
              </DetailTopBarSub>
            </DetailTopBarInfo>
            <IconBtn onClick={() => setShowSearch(true)}>
              <SearchIcon />
            </IconBtn>
            <IconBtn onClick={() => setShowContact(true)}>
              <CallIcon />
            </IconBtn>
          </>
        )}
      </DetailTopBar>

      <DetailContent>
        {showProductDetails ? renderReadOnlyProductDetails() : (
          <>
        {/* ── Header card ── */}
        {!isSearchMode && renderHeaderCard()}

        {/* ── Upcoming: read-only product list ── */}
        {!isSearchMode && isUpcoming && (
          <>
            <div className="bid-details-products-heading bid-details-products-heading-wrap-tight">
              {products.length} products
            </div>
            <SectionCard>
              {products.map((p) => (
                <ProductListItem key={p.id}>
                  <ProductImg>
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-12" />
                      : <ProductImagePlaceholder />}
                  </ProductImg>
                  <ProductInfo>
                    <ProductName>{p.name}</ProductName>
                    <ProductMeta>Min qty: {p.minQty} • Total qty: {p.totalQty}</ProductMeta>
                  </ProductInfo>
                </ProductListItem>
              ))}
            </SectionCard>
            {renderBidDetailsCard({ showEmptyMessage: false })}
          </>
        )}

        {/* ── Live: interactive product cards ── */}
        {isLive && (
          <>
            {!isSearchMode && <div className="bid-details-products-heading-wrap">
              <div className="bid-details-products-heading">{products.length} products</div>
              {addedCount > 0 && (
                <div className="bid-details-products-subheading">{addedCount} product{addedCount > 1 ? "s'" : "'"} price added</div>
              )}
            </div>}

            {filteredProducts.map((p) => {
              const bid = localBids[p.bidding_session_item_id];
              const isAdded = !!bid;
              if (isAdded) {
                return (
                  <SectionCard key={p.id} className="bid-details-bid-card">
                    <div className="bid-details-bid-card-row">
                      <ProductImg className="bid-details-product-img-medium bid-details-product-img-medium-soft">
                        {p.imageUrl
                          ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-8" />
                          : <ProductImagePlaceholder />}
                      </ProductImg>
                      <ProductName className="bid-details-bid-card-product-name">
                        {p.name}
                      </ProductName>
                      <span className="bid-details-added-pill">
                        <Check size={14} strokeWidth={2.6} />
                        Added
                      </span>
                    </div>

                    <div className="bid-details-dashed-divider" />

                    <div className="bid-details-live-bid-summary-row">
                      <div className="bid-details-live-bid-values">
                        <div>
                          <div className="bid-details-bid-value-label">Quantity:</div>
                          <div className="bid-details-bid-value">{bid.quantity_offered}{p.unit}</div>
                        </div>
                        <div>
                          <div className="bid-details-bid-value-label">Price/{p.unit}:</div>
                          <div className="bid-details-bid-value">₹{bid.price_offered}</div>
                          {p.taxRate ? (
                            <div className="bid-details-bid-tax-label">incl. {p.taxRate}% tax</div>
                          ) : null}
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveProduct(p)}
                        className="bid-details-edit-bid-button"
                      >
                        Edit bid <ChevronRight size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </SectionCard>
                );
              }

              return (
                <LiveProductActionCard key={p.id}>
                  <LiveProductActionInfo>
                    <ProductImg>
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt={p.name} className="bid-details-product-image bid-details-product-image-radius-12" />
                        : <ProductImagePlaceholder />}
                    </ProductImg>
                    <ProductInfo>
                      <div className="bid-details-live-product-title-row">
                        <ProductName className="bid-details-flex-one">{p.name}</ProductName>
                        {isAdded && (
                          <span className="bid-details-live-added-pill">
                            ✓ Added
                          </span>
                        )}
                      </div>
                      {isAdded ? (
                        <div className="bid-details-live-inline-values">
                          <div>
                            <div className="bid-details-inline-value-label">Quantity:</div>
                            <div className="bid-details-inline-value">{bid.quantity_offered}{p.unit}</div>
                          </div>
                          <div>
                            <div className="bid-details-inline-value-label">Price/{p.unit}:</div>
                            <div className="bid-details-inline-value">₹{bid.price_offered}</div>
                          </div>
                        </div>
                      ) : (
                        <ProductMeta>Min qty: {p.minQty} • Total qty: {p.totalQty}</ProductMeta>
                      )}
                    </ProductInfo>
                  </LiveProductActionInfo>

                  <LiveProductActionDivider />
                  <LiveProductActionButtonWrap>
                    <LiveProductActionButton
                      onClick={() => setActiveProduct(p)}
                    >
                      {isAdded ? <>View bid <span className="bid-details-action-arrow">›</span></> : <>Add price &amp; quantity <span className="bid-details-action-arrow">›</span></>}
                    </LiveProductActionButton>
                  </LiveProductActionButtonWrap>
                </LiveProductActionCard>
              );
            })}

            <div className="bid-details-bottom-spacer-large" />
          </>
        )}

        {/* ── Awarded: accordions ── */}
        {!isSearchMode && isAwarded && (
          <>
            {renderBidDetailsCard({ showEmptyMessage: false })}
            {allotments.length > 0 && (
              <>
                <SectionDividerLabel>AWARDED DETAILS</SectionDividerLabel>
                <SectionCard>
                  {(() => {
                    const awardedProducts = allotments.flatMap(a => (a.allotment_items || []).map(ai => {
                      const sessionItem = session.rawItems.find(i => i.id === ai.bidding_session_item_id);
                      return {
                        id: ai.id || ai.bidding_session_item_id,
                        imageUrl: sessionItem?.image_url || sessionItem?.icon_url || sessionItem?.imageUrl || null,
                        name: sessionItem?.name || `Item ${ai.bidding_session_item_id}`,
                        qty: `${ai.allotted_quantity ?? "—"}kg`,
                        price: ai.allotted_price ? `₹${ai.allotted_price}/kg` : "",
                      };
                    }));
                    return (
                      <Accordion
                        title={`Awarded bid for ${awardedProducts.length} products`}
                        products={awardedProducts}
                        defaultOpen={true}
                      />
                    );
                  })()}
                </SectionCard>
              </>
            )}
          </>
        )}

        {/* ── Submitted / not awarded / no submission ── */}
        {!isSearchMode && isSubmittedOrNotAwarded && (
          <>
            {/* Payment details — only when there are fees from config */}
            {(processingFee || securityDeposit || totalFee) && (
              <SectionCard>
                <SectionDividerLabel>PAYMENT DETAILS</SectionDividerLabel>
                {processingFee && (
                  <div className="bid-details-payment-item">
                    <div className="bid-details-payment-row">
                      <span className="bid-details-payment-label">Processing fee</span>
                      <span className="bid-details-payment-value">₹{processingFee}</span>
                    </div>
                    <div className="bid-details-payment-note">2.5% of estimated business opportunity</div>
                  </div>
                )}
                {securityDeposit != null && (
                  <div className="bid-details-payment-row bid-details-payment-item">
                    <span className="bid-details-payment-label">Security deposit</span>
                    <span className="bid-details-payment-value">₹{securityDeposit}</span>
                  </div>
                )}
                {totalFee && (
                  <div className="bid-details-payment-row bid-details-payment-item">
                    <span className="bid-details-payment-total-label">Total amount</span>
                    <span className="bid-details-payment-total-value">₹{totalFee}</span>
                  </div>
                )}
                <div className="bid-details-payment-refund-note">
                  <span className="bid-details-payment-info-icon">ℹ️</span>
                  <span className="bid-details-payment-refund-text">Fully refunded if bid is not awarded</span>
                </div>
              </SectionCard>
            )}

            {renderBidDetailsCard()}
          </>
        )}
          </>
        )}
      </DetailContent>

      {/* ── Sticky CTA (live only) ── */}
      {isLive && (
        <div className="bid-details-sticky-cta">
          {hasModifiedBidItems && (
            <button
              disabled={upsertBidStatus === "BEGIN"}
              onClick={() => handleSaveProgress()}
              className="bid-details-save-progress-button"
            >
              <SaveIcon color="#ef4f5f" />
              {upsertBidStatus === "BEGIN" ? "Saving…" : "Save progress"}
            </button>
          )}
          <CtaButton
            disabled={!canContinue || upsertBidStatus === "BEGIN" || termsLoading}
            onClick={handleContinue}
            className="bid-details-cta-button"
          >
            {upsertBidStatus === "BEGIN" || termsLoading ? "Loading…" : "Continue"}
          </CtaButton>
        </div>
      )}

      {/* ── Bid terms & conditions sheet ── */}
      {showTerms && isLive && (
        <Overlay onClick={() => setShowTerms(false)}>
          <div
            onClick={e => e.stopPropagation()}
            className="bid-details-terms-sheet"
          >
            <div className="bid-details-sheet-handle" />
            <div className="bid-details-terms-title">Bid terms &amp; conditions</div>

            {/* warning banner */}
            <div className="bid-details-terms-warning">
              <span className="bid-details-terms-warning-icon">⚠️</span>
              <span className="bid-details-terms-warning-text">
                Failure to meet these terms may lead to product rejection and cancellation of this or all your bids
              </span>
            </div>

            {/* terms description */}
            {termsDescription ? (
              termsDescriptionLines.map((line, index) => (
                <div key={`${line}-${index}`} className="bid-details-term-row">
                  <span className="bid-details-term-bullet">•</span>
                  <div className="bid-details-term-text">
                    {line}
                  </div>
                </div>
              ))
            ) : (
              [
                { title: "Quality Compliance", body: "Must meet defined QC specs (grade, size, freshness); deviations lead to rejection" },
                { title: "Price Commitment", body: "Bid price is final, inclusive of all costs; no post-award changes" },
                { title: "Fulfilment SLA", body: "100% quantity delivery within agreed timeline; delays/shortfalls penalised" },
                { title: "Quantity Accuracy", body: "Delivered weight/quantity must match PO within allowed tolerance" },
                { title: "Packaging Standards", body: "Use approved packaging; damage/leakage is vendor liability" },
                { title: "Compliance & Documentation", body: "Must follow FSSAI norms and provide valid invoice & details" },
              ].map((t) => (
                <div key={t.title} className="bid-details-term-row">
                  <span className="bid-details-term-bullet">•</span>
                  <div className="bid-details-term-text">
                    <strong className="bid-details-term-title">{t.title}:</strong> {t.body}
                  </div>
                </div>
              ))
            )}

            {/* action buttons */}
            <div className="bid-details-terms-actions">
              <button
                onClick={() => setShowTerms(false)}
                className="bid-details-terms-secondary-button"
              >
                I don&apos;t agree
              </button>
              <button
                onClick={handleAgreeTerms}
                disabled={upsertBidStatus === "BEGIN"}
                className="bid-details-terms-primary-button"
              >
                {upsertBidStatus === "BEGIN" ? "Submitting…" : "I agree"}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Add price & quantity sheet ── */}
      {activeProduct && (isLive || isSubmittedOrNotAwarded || isAwarded) && (
        <AddPriceSheet
          product={activeProduct}
          existingBid={activeProduct.existingBid || localBids[activeProduct.bidding_session_item_id] || null}
          onSave={handleSaveBid}
          onDelete={handleDeleteBid}
          onClose={() => setActiveProduct(null)}
          readOnly={!isLive}
        />
      )}

      {/* ── Contact modal ── */}
      {showContact && (
        <Overlay onClick={() => setShowContact(false)} className="bid-details-contact-overlay">
          <div
            onClick={e => e.stopPropagation()}
            className="bid-details-contact-modal"
          >
            <div className="bid-details-contact-title">Contact bid creator</div>
            <div className="bid-details-contact-subtitle">to ask more about the bid</div>

            <div className="bid-details-contact-row">
              <div className="bid-details-contact-avatar">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="8" r="4" stroke="#999" strokeWidth="1.5" fill="none"/>
                  <path d="M3 17c0-3.3 3.1-6 7-6s7 2.7 7 6" stroke="#999" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                </svg>
              </div>
              <div className="bid-details-contact-info">
                <div className="bid-details-contact-label">Bid creator</div>
                <div className="bid-details-contact-name">{contactInfo.name || "—"}</div>
              </div>
              {contactInfo.phone && (
                <a href={`tel:${contactInfo.phone}`} className="bid-details-contact-call-link">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M4 2h4l2 5-2.5 1.5C8.5 11 9 11.5 11.5 13.5L13 11l5 2v4c0 1-5 3-11-3S3 3 4 2z" stroke="#ef4f5f" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                  </svg>
                </a>
              )}
            </div>

            <button
              onClick={() => setShowContact(false)}
              className="bid-details-contact-ok-button"
            >
              Okay
            </button>
          </div>
        </Overlay>
      )}
    </DetailPageWrapper>
  );
};

export default BidDetails;
